import React, { useContext, useEffect, useState, useRef } from 'react';
import { Button, Card, Collapse, Icon, Input, List, Tabs, Typography, Upload, Select, Switch, Popover } from 'antd';
import { HexColorPicker } from 'react-colorful';
import GlobalStateContext from '../../../../context';
import FileUpload from '../../../../components/FileUpload';
import MediapanelWebService from '../../../../classes/MediapanelWebService';
import MediaPannelBanner from './mediaPannelBanners/MediaPannelBanner';
import { BANNER_TYPE_IMAGE } from '../../../../constants';
import { IoMdAdd } from "react-icons/io";
import './MediaPannelDesign.css';
import BreakingNews from './mediaPannelBanners/breakingnews/BreakingNews';
import ClassicBanner from './mediaPannelBanners/classic/ClassicBanner';
import Themes from './themes/Themes';

const { Panel } = Collapse;
const { Option } = Select;

function MediaPannelDesign(props) {

    const { designs, active, stream_id, state, onAdd, onDelete, onPresentMedia, onStopPresenting } = props;
    const [_designs, setDesigns] = useState(designs || {});
    const { themes, fonts, overlays, backgrounds, banners, videos, logos, logoPositions, logoSizes, music } = designs || {};
    const [gState, dispatch] = useContext(GlobalStateContext);
    const mpws = new MediapanelWebService();
    const isInitialized = useRef(false);
    const prevStateRef = useRef(null);


    // Initialize with existing stream configuration - only run once
    useEffect(() => {
        // Only run if state has actually changed and we haven't initialized yet
        const stateChanged = JSON.stringify(state) !== JSON.stringify(prevStateRef.current);
        if (state && Object.keys(state).length > 0 && !isInitialized.current && stateChanged) {
            console.log("[Design] Initializing with existing configuration");
            isInitialized.current = true;
            prevStateRef.current = JSON.stringify(state);
        }
    }, [state]);

    // Handle active prop changes (fallback for legacy support) - only run once
    useEffect(() => {
        if (active && Object.keys(active).length > 0 && (!state || Object.keys(state).length === 0) && !isInitialized.current) {
            console.log("[Design] Initializing with active configuration");
            isInitialized.current = true;
        }
    }, [active, state]);

    // Reset initialization flag when component unmounts or stream_id changes
    useEffect(() => {
        return () => {
            isInitialized.current = false;
        };
    }, [stream_id]);



    // Centralized function to update stream config
    const updateStreamConfig = (updates) => {
        const newConfig = {
            stream_id: stream_id,
            ...state,
            ...updates
        };

        console.log("[MediaPannelDesign] updateStreamConfig - updates:", updates);
        console.log("[MediaPannelDesign] updateStreamConfig - newConfig:", newConfig);
        console.log("[MediaPannelDesign] updateStreamConfig - logo visibility:", newConfig.logo?.visible);

        // Update stream state in parent component
        if (props.onConfigUpdate) {
            props.onConfigUpdate(newConfig);
        }
    };



    // Branding handlers (from original MediaPannelBranding)
    const handleUpload = (type, data) => {
        const newItem = { url: data?.location, label: data?.key, name: data?.originalname };
        props?.onAdd && onAdd(type, newItem)

        setDesigns(prev => ({
            ...prev,
            [type]: [newItem, ...(prev[type] || [])]
        }));



    };

    const handleUseItem = (type, item) => {
        console.log("[x] handleUseItem", item)
        const updates = {
            [type]: item

        }

        // Update the stream config
        updateStreamConfig(updates);
    };

    return (
        <div className="modern-design-panel p-0">
            <Collapse
                defaultActiveKey={[]}
                accordion={false}
                className="design-collapse"
                expandIconPosition="right"
            >
                {/* Theme Section */}
                <Panel
                    header={
                        <span className="panel-header">

                            <span className="panel-title">Theme</span>

                        </span>
                    }
                    key="theme"
                    className="theme-panel"
                >
                    <div className="theme-section">

                        <div className='flex flex-row gap-2'>

                            <div className="theme-option">
                                <label className="theme-label">Primary Color</label>
                                <div className="color-picker-container">
                                    <Popover
                                        content={
                                            <div className="color-picker-popover">
                                                <HexColorPicker
                                                    color={state?.theme?.primaryColor || '#266EFF'}
                                                    onChange={color => handleUseItem('theme', { ...state?.theme, primaryColor: color })}
                                                />
                                            </div>
                                        }
                                        trigger="click"
                                        placement="bottomLeft"
                                        overlayClassName="color-picker-overlay"
                                    >
                                        <div className="color-picker-trigger">
                                            <div
                                                className="color-preview"
                                                style={{ backgroundColor: state?.theme?.primaryColor || '#266EFF' }}
                                            />
                                            <Input
                                                value={state?.theme?.primaryColor || '#266EFF'}
                                                onChange={(color) => handleUseItem('theme', { ...state?.theme, primaryColor: color })}
                                                placeholder="#266EFF"
                                                className="color-input"
                                            />
                                        </div>
                                    </Popover>
                                </div>
                            </div>
                            <div className="theme-option">
                                <label className="theme-label">Secondary Color</label>
                                <div className="color-picker-container">
                                    <Popover
                                        content={
                                            <div className="color-picker-popover">
                                                <HexColorPicker
                                                    color={state?.theme?.secondaryColor || '#000000'}
                                                    onChange={color => handleUseItem('theme', { ...state?.theme, secondaryColor: color })}
                                                />
                                            </div>
                                        }
                                        trigger="click"
                                        placement="bottomLeft"
                                        overlayClassName="color-picker-overlay"
                                    >
                                        <div className="color-picker-trigger">
                                            <div
                                                className="color-preview"
                                                style={{ backgroundColor: state?.theme?.secondaryColor || '#000000' }}
                                            />
                                            <Input
                                                value={state?.theme?.secondaryColor || '#000000'}
                                                onChange={(color) => handleUseItem('theme', { ...state?.theme, secondaryColor: color })}
                                                placeholder="#000000"
                                                className="color-input"
                                            />
                                        </div>
                                    </Popover>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-row gap-2">
                            <div className="theme-option">
                                <label className="theme-label">Font</label>
                                <Select
                                    value={state?.theme?.font}
                                    onChange={(font) => handleUseItem('theme', { ...state?.theme, font })}
                                    className="font-select"
                                >
                                    {fonts?.map(font => (
                                        <Option key={font} value={font}>{font}</Option>
                                    ))}
                                </Select>
                            </div>

                        </div>
                        <div className="theme-option">
                            <label className="theme-label">Theme Style</label>
                            <div className="flex flex-col gap-2">
                                {themes?.map(theme => (
                                    <div className='border-2' key={theme.name}>
                                        <Themes
                                            state={state?.theme}
                                            key={theme.name}
                                            name={theme.name}
                                            title={theme.label}
                                            text={theme.name}
                                            onClick={() => handleUseItem('theme', { ...state?.theme, name: theme.name })}
                                            active={state?.theme?.name === theme.name}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="theme-option">
                            <label className="theme-label">Label Position</label>
                            <Select
                                value={state?.labelPosition || 'bottom-left'}
                                onChange={position => updateStreamConfig({ labelPosition: position })}
                                className="font-select"
                            >
                                <Option value="top-left">Top Left</Option>
                                <Option value="top-right">Top Right</Option>
                                <Option value="bottom-left">Bottom Left</Option>
                                <Option value="bottom-right">Bottom Right</Option>
                            </Select>
                            <div className="text-xs text-gray-500 mt-1">
                                Position stream labels to avoid overlap with banners
                            </div>
                        </div>
                    </div>
                </Panel>

                {/* Logo Section */}
                <Panel
                    header={
                        <span className="panel-header">

                            <span className="panel-title">Logo </span>
                            <span className="panel-count">{logos?.length || 0} items</span>
                        </span>
                    }
                    key="logo"
                    className="logo-panel"
                >
                    <div className="logo-section">
                        {/* Logo Grid with Upload */}
                        <div className="backdrops-grid">
                            {/* Upload Item */}
                            <FileUpload
                                allowedTypes={['image/jpeg', 'image/png', 'image/svg+xml']}
                                maxFileSize={2}
                                showUploadList={false}
                                onComplete={res => handleUpload("logos", res.fileList[0]?.response?.data)}
                                className="upload-container"
                                preview={false}
                                text="Upload Logo"
                                data={{ bucket: "viblica-assets" }}
                            >
                                <div className="backdrop-item upload-item">
                                    <div className="upload-content">
                                        <IoMdAdd className="upload-plus-icon" />
                                        <div className="upload-text">Add Logo</div>
                                    </div>
                                </div>
                            </FileUpload>

                            {/* Logo Items */}
                            {logos?.map((item, index) => {
                                const isActive = state?.logo?.url === item.url;

                                return (
                                    <div
                                        key={item.url || index}
                                        className={`backdrop-item ${isActive ? 'active' : ''}`}
                                        style={{
                                            backgroundImage: `url(${item.url})`,
                                            backgroundSize: 'cover',
                                            backgroundPosition: 'center'
                                        }}
                                        onClick={() => handleUseItem("logo", item)}
                                    >
                                        <div className="backdrop-overlay">
                                            {isActive && (
                                                <div className="active-indicator">
                                                    <Icon type="check" />
                                                </div>
                                            )}
                                            {(item.owner === gState?.user?._id || item.user_id === gState?.user?._id) && (
                                                <button
                                                    className="delete-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        console.log("[x] Deleting logo", item)
                                                        props.onDelete("logos", item);
                                                    }}
                                                    title="Delete logo"
                                                >
                                                    <Icon type="delete" />
                                                </button>
                                            )}
                                        </div>
                                        {/* <div className="backdrop-name">
                                                {item.name || `Logo ${index + 1}`}
                                            </div> */}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Logo Settings */}
                        <div className="logo-settings">
                            <div className="setting-group">
                                <label className="setting-label">Position</label>
                                <Select
                                    value={state?.logo?.position || 'top-left'}
                                    onChange={position => handleUseItem('logo', { ...state?.logo, position })}
                                    className="setting-select"
                                >
                                    {logoPositions?.map(pos => (
                                        <Option key={pos.value} value={pos.value}>{pos.label}</Option>
                                    ))}
                                </Select>
                            </div>

                            <div className="setting-group">
                                <label className="setting-label">Size</label>
                                <Select
                                    value={state?.logo?.size || 'small'}
                                    onChange={size => handleUseItem('logo', { ...state?.logo, size })}
                                    className="setting-select"
                                >
                                    {logoSizes?.map(size => (
                                        <Option key={size.value} value={size.value}>{size.label}</Option>
                                    ))}
                                </Select>
                            </div>

                            <div className="setting-group">
                                <label className="setting-label">Visible</label>
                                <Switch
                                    checked={state?.logo?.visible !== false}
                                    onChange={(checked) => {
                                        console.log("[x] checked", checked)
                                        handleUseItem('logo', { ...state?.logo, visible: checked })
                                    }}
                                    checkedChildren="On"
                                    unCheckedChildren="Off"
                                />
                            </div>
                        </div>
                    </div>
                </Panel>

                {/* Branding Section (Backdrops) */}
                <Panel
                    header={
                        <span className="panel-header">

                            <span className="panel-title">Backgrounds</span>
                            <span className="panel-count">
                                {backgrounds?.length || 0} items
                            </span>
                        </span>
                    }
                    key="backgrounds"
                    className=""
                >
                    {/* Backdrops Grid with Upload */}
                    <div className="backdrops-grid">
                        {/* Upload Item */}
                        <FileUpload
                            allowedTypes={['image/jpeg', 'image/png']}
                            maxFileSize={2}
                            showUploadList={false}
                            onComplete={res => handleUpload("backgrounds", res.fileList[0]?.response?.data)}
                            className="upload-container"
                            preview={false}
                            text="Upload Background"
                            data={{ bucket: "viblica-assets" }}
                        >
                            <div className="backdrop-item upload-item">
                                <div className="upload-content">
                                    <IoMdAdd className="upload-plus-icon" />
                                    <div className="upload-text">Add Background</div>
                                </div>
                            </div>
                        </FileUpload>

                        {/* Background Items */}
                        {backgrounds?.map((item, index) => {
                            const isActive = state?.background?.url === item.url;

                            return (
                                <div
                                    key={item.url || index}
                                    className={`backdrop-item ${isActive ? 'active' : ''}`}
                                    style={{
                                        backgroundImage: `url(${item.url})`,
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center'
                                    }}
                                    onClick={() => handleUseItem("background", item)}
                                >
                                    <div className="backdrop-overlay">
                                        {isActive && (
                                            <div className="active-indicator">
                                                <Icon type="check" />
                                            </div>
                                        )}
                                        {(item.owner === gState?.user?._id || item.user_id === gState?.user?._id) && (
                                            <button
                                                className="delete-btn"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    console.log("[x] Deleting logo", item)
                                                    props.onDelete("backgrounds", item);
                                                }}
                                                title="Delete logo"
                                            >
                                                <Icon type="delete" />
                                            </button>
                                        )}
                                    </div>
                                    {/* <div className="backdrop-name">
                                            {item.name || `Background ${index + 1}`}
                                        </div> */}
                                </div>
                            );
                        })}
                    </div>

                    {/* Empty State - only show if no backgrounds */}
                    {(!backgrounds || backgrounds?.length === 0) && (
                        <div className="empty-state">

                            <div className="empty-text">No backgrounds uploaded yet</div>
                            <div className="empty-hint">Upload your first background to get started</div>
                        </div>
                    )}
                </Panel>

                {/* Video Clips Section */}
                <Panel
                    header={
                        <span className="panel-header">

                            <span className="panel-title">Video Clips</span>
                            <span className="panel-count">
                                {videos?.length || 0} items
                            </span>
                        </span>
                    }
                    key="videos"
                    className="video-clips-panel"
                >
                    {/* Video Clips Grid with Upload */}
                    <div className="video-clips-grid">
                        {/* Upload Item */}
                        <FileUpload
                            allowedTypes={['video/mp4', 'video/webm', 'video/ogg']}
                            maxFileSize={50}
                            showUploadList={false}
                            onComplete={res => handleUpload("videos", res.fileList[0]?.response?.data)}
                            className="upload-container"
                            preview={false}
                            text="Upload Video Clip"
                            data={{ bucket: "viblica-assets" }}
                        >
                            <div className="video-clip-item upload-item">
                                <div className="upload-content">
                                    <IoMdAdd className="upload-plus-icon" />
                                    <div className="upload-text">Add Video</div>
                                </div>
                            </div>
                        </FileUpload>

                        {/* Video Clip Items */}
                        {videos?.map((clip, index) => (
                            <div key={index} className="video-clip-item">
                                <div className="video-clip-preview">
                                    <video
                                        src={clip.url}
                                        className="video-preview"
                                        muted
                                    />
                                    <div className="video-overlay">
                                        <Icon type="play-circle" className="play-icon" />
                                    </div>
                                </div>
                                <div className="video-clip-info">
                                    <div className="video-clip-name">
                                        {clip.name || `Video Clip ${index + 1}`}
                                    </div>
                                    <div className="video-action-buttons">
                                        {state?.sharedMedia?.url === clip.url ? (
                                            <button
                                                className="present-btn presenting"
                                                onClick={() => onStopPresenting && onStopPresenting()}
                                                title="Stop presenting"
                                            >
                                                <Icon type="stop" />
                                            </button>
                                        ) : (
                                            <button
                                                className="present-btn"
                                                onClick={() => onPresentMedia && onPresentMedia("videos", clip)}
                                                title="Present to all participants"
                                            >
                                                <Icon type="play-circle" />
                                            </button>
                                        )}
                                        {(clip.owner === gState?.user?._id || clip.user_id === gState?.user?._id) && (
                                            <button
                                                className="delete-btn"
                                                onClick={() => props.onDelete("videos", clip)}
                                                title="Delete video clip"
                                            >
                                                <Icon type="delete" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Empty State - only show if no videos */}
                    {(!videos || videos?.length === 0) && (
                        <div className="empty-state">
                            <Icon type="video-camera" className="empty-icon" />
                            <div className="empty-text">No video clips uploaded yet</div>
                            <div className="empty-hint">Upload your first video clip to get started</div>
                        </div>
                    )}
                </Panel>
            </Collapse>
        </div>
    );
}

export default MediaPannelDesign; 